import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Home, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { useState } from "react";
import bellabotImg from "@/assets/robots/bellabot.jpg";
import bellabotProImg from "@/assets/robots/bellabot-pro.jpg";
import kettybotProImg from "@/assets/robots/kettybot-pro-variants.jpeg";
import pudubot2Img from "@/assets/robots/pudubot-2.jpg";
import cc1Img from "@/assets/robots/cc1.jpg";
import cc1ProImg from "@/assets/robots/cc1-pro-main.jpg";
import mt1Img from "@/assets/robots/mt1-vac-hero.jpeg";
import mt1MaxImg from "@/assets/robots/mt1-max-hero.jpeg";
import sh1Img from "@/assets/robots/sh1-main.png";
import flashbotArmImg from "@/assets/robots/flashbot-arm-main.jpeg";
import puduD9Img from "@/assets/robots/pudu-d9.png";
import t300Img from "@/assets/robots/t300-hero.jpeg";
import holabotImg from "@/assets/robots/holabot.png";
import mt1ReddotImg from "@/assets/robots/mt1-hero.jpeg";
import flashbotMaxImg from "@/assets/robots/flashbot-max-new.jpeg";
import puduD7Img from "@/assets/robots/pudu-d7.png";
import t600Img from "@/assets/robots/pudu-t600-updated.png";
import t600UnderrideImg from "@/assets/robots/pudu-t600-underride.png";
import swiftbotImg from "@/assets/robots/swiftbot-hygiene.jpeg";
import bellabotProVideo from "@/assets/robots/bellabot-pro-demo.mp4";
import puduD5Img from "@/assets/robots/pudu-d5-hero.png";
import puduBG1Img from "@/assets/robots/pudu-bg1-hero.png";

interface Robot {
  name: string;
  category: string;
  description: string;
  image: string;
  features: string[];
  detailPage?: string;
  isNew?: boolean;
  isPreOrder?: boolean;
  video?: string;
  imageObjectFit?: "cover" | "contain";
}

const Robots = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const commercialDeliveryRobots: Robot[] = [
    {
      name: "BellaBot",
      category: "Premium Delivery Robot",
      description: "Premium delivery robot with multi-modal interaction and smart tray detection.",
      image: bellabotImg,
      features: [
        "Multi-modal Interaction",
        "Smart Tray Detection", 
        "65cm Superior Mobility",
        "Industry-First Battery Swapping Technology"
      ],
      detailPage: "/robots/bellabot"
    },
    {
      name: "BellaBot Pro",
      category: "Premium Delivery & Advertising Robot",
      description: "Enhanced delivery robot with 18.5-inch advertising screen and extended battery life.",
      image: bellabotProImg,
      features: [
        "18.5-inch Advertising Screen",
        "Smart Trays Detection",
        "52cm Superior Mobility",
        "Extended Battery Life"
      ],
      detailPage: "/robots/bellabot-pro",
      isNew: true,
      video: bellabotProVideo
    },
    {
      name: "KettyBot Pro",
      category: "Delivery & Advertising Robot",
      description: "Versatile delivery and advertising robot with in-store marketing capabilities and smart tray detection.",
      image: kettybotProImg,
      features: [
        "In-store Marketing with Customized Advertisement Display",
        "Smart Trays Detection with 360° Fisheye Lenses",
        "PUDU VSLAM+ for Marker-less Deployment",
        "Seamless Delivery & Autonomous Load Detection"
      ],
      detailPage: "/robots/kettybot-pro",
      isNew: true
    },
    {
      name: "PuduBot 2",
      category: "Universal Delivery Robot",
      description: "Award-winning universal delivery robot with smart dot matrix screen.",
      image: pudubot2Img,
      features: [
        "Smart Dot Matrix Screen",
        "Four-Layer Infrared Tray Sensor",
        "Advanced Navigation",
        "IDEA Award Winner"
      ],
      detailPage: "/robots/pudubot-2",
      isNew: true
    },
    {
      name: "PUDU HolaBot",
      category: "Pager & Notification Delivery Robot",
      description: "Compact delivery robot perfect for paging and notification delivery in restaurants and hospitality.",
      image: holabotImg,
      features: [
        "Paging System Integration",
        "Compact Design",
        "Smart Navigation",
        "Multiple Notification Modes"
      ],
      detailPage: "/robots/holabot"
    },
    {
      name: "FlashBot Max",
      category: "Building Delivery Expert",
      description: "Advanced building delivery robot with VSLAM+ semi-outdoor navigation, full IoT integration, and modular compartments for seamless multi-floor operations.",
      image: flashbotMaxImg,
      features: [
        "VSLAM+ Semi-outdoor Delivery - Navigate complex semi-open areas",
        "Full-Scope IoT Integration - Automatic elevator & turnstile access",
        "Modular Compartments - 2-4 adjustable compartments with ventilation",
        "60cm Path Clearance - Effortlessly navigate narrow corridors",
        "9-Hour Runtime - 4-hour charging with auto power charging",
        "Multi-Compartment Security - Password, phone & NFC verification"
      ],
      detailPage: "/robots/flashbot-max",
      isNew: true
    },
    {
      name: "SwiftBot",
      category: "Hygienic Delivery Robot for Medical Industry",
      description: "Award-winning versatile delivery robot with flexible configuration options and specialized hygiene delivery modules for healthcare and medical facilities. iF Design Award 2023 winner.",
      image: swiftbotImg,
      features: [
        "iF Design Award 2023 Winner",
        "Hygienic Delivery Module - Medical-grade enclosed compartments for sterile transport",
        "Flexible Configuration - Multiple attachment modes for diverse medical needs",
        "UV Sterilization Ready - Optional UV-C disinfection for high-hygiene environments",
        "Sealed Compartments - Prevent contamination during medicine and sample transport",
        "Hospital-Grade Materials - Easy-to-clean surfaces meeting medical standards",
        "Efficient Multi-Purpose Delivery - Medications, lab samples, supplies, and linens"
      ],
      detailPage: "/robots/swiftbot",
      isNew: true
    }
  ];

  const industrialDeliveryRobots: Robot[] = [
    {
      name: "FlashBot Max",
      category: "Building Delivery Expert",
      description: "Advanced building delivery robot with VSLAM+ semi-outdoor navigation, full IoT integration, and modular compartments for seamless multi-floor operations.",
      image: flashbotMaxImg,
      features: [
        "VSLAM+ Semi-outdoor Delivery - Navigate complex semi-open areas",
        "Full-Scope IoT Integration - Automatic elevator & turnstile access",
        "Modular Compartments - 2-4 adjustable compartments with ventilation",
        "60cm Path Clearance - Effortlessly navigate narrow corridors",
        "9-Hour Runtime - 4-hour charging with auto power charging",
        "Multi-Compartment Security - Password, phone & NFC verification"
      ],
      isNew: true
    },
    {
      name: "PUDU T600",
      category: "Heavy-payload Industrial Delivery Robot",
      description: "Industrial delivery robot with 600kg payload capacity, 12-hour battery life, and built-in touch screen. Features VSLAM + LiDAR navigation, multi-floor capability, and VDA5050 protocol support.",
      image: t600Img,
      features: [
        "600kg payload capacity",
        "12-hour battery life with 2-hour fast charging",
        "Built-in touch screen operation",
        "Multi-floor elevator control",
        "Rack group recognition",
        "Ground projection safety system"
      ],
      detailPage: "/robots/pudu-t600",
      isNew: true,
      imageObjectFit: "contain"
    },
    {
      name: "PUDU T600 Underride",
      category: "Flexible & Efficient Industrial Delivery Platform",
      description: "Low-profile chassis design that navigates beneath racks and lifts them autonomously. 600kg capacity with compact 255mm height for flexible material handling.",
      image: t600UnderrideImg,
      features: [
        "Two modes, Freely Selectable",
        "Flexible Configuration",
        "Industrial Grade Durability",
        "On-premises Deployment"
      ],
      detailPage: "/robots/pudu-t600",
      isNew: true,
      imageObjectFit: "contain"
    },
    {
      name: "PUDU T300 Series",
      category: "Industrial Delivery Robot",
      description: "Award-winning industrial delivery robot with 300kg payload capacity, intelligent follow mode, and 5 versatile add-on accessories. Red Dot Winner 2025.",
      image: t300Img,
      features: [
        "Red Dot Design Award Winner 2025",
        "300kg Load Capacity - Industry-leading payload",
        "Intelligent Follow Mode - Reduces physical strain by following workers automatically with hands-free operation and blue ground projection for enhanced safety awareness",
        "5 Add-On Accessories - T300 Base, Tray, Lifting, Towing & Conveyor",
        "Power-Assist Mode - Manual push with motorized assistance for effortless maneuvering",
        "Auto-Delivery Mode - Fully autonomous point-to-point delivery",
        "Flexible VSLAM+ Deployment - Marker-less navigation",
        "60cm Superior Mobility - Navigate tight spaces",
        "7/24 Non-stop Operation - Up to 72+ hours continuous performance",
        "ISO 3691-4 Safety Compliant - Industrial-grade safety standards"
      ],
      detailPage: "/robots/t300-series",
      isNew: true
    }
  ];

  const humanoidRobots: Robot[] = [
    {
      name: "PUDU D9",
      category: "Full-sized Bipedal Humanoid Robot",
      description: "The first full-sized bipedal humanoid robot by Pudu Robotics. 170cm height, 42 DOF, designed to match adult body proportions with high-precision perception and advanced mobility.",
      image: puduD9Img,
      features: [
        "170cm Height, 65kg Weight - Adult body proportions",
        "42 Independent Degrees of Freedom with 352 N·m Max Joint Torque",
        "20kg Maximum Payload with Dexterous Hands (PUDU DH11)",
        "275 TOPS Computing Power - 2 m/s Maximum Walking Speed"
      ],
      detailPage: "/robots/pudu-d9",
      isNew: true,
      isPreOrder: true,
      imageObjectFit: "contain"
    },
    {
      name: "PUDU D7",
      category: "Next-Generation Industrial Semi-Humanoid Robot",
      description: "Next-generation industrial semi-humanoid robot powered by PuduFM 1.0 embodied intelligence — built for manufacturing, warehouse handling, shelf picking, and intralogistics.",
      image: puduD7Img,
      features: [
        "Powered by PuduFM 1.0 embodied intelligence foundation model",
        "Built for industrial, manufacturing, and warehouse environments",
        "Shelf picking, material movement, and inventory replenishment",
        "Advanced perception, dexterous manipulation, and continuous learning"
      ],
      detailPage: "/robots/pudu-d7",
      isNew: true,
      imageObjectFit: "contain"
    }
  ];

  const cleaningRobots: Robot[] = [
    {
      name: "PUDU CC1",
      category: "Intelligent Commercial Cleaning Robot",
      description: "4-in-1 versatile cleaning with auto charging and water management.",
      image: cc1Img,
      features: [
        "4-in-1 Versatile Cleaning",
        "Auto Charging & Water Addition",
        "Diversified Consumables",
        "Dynamic Space Perception"
      ],
      detailPage: "/robots/cc1"
    },
    {
      name: "PUDU CC1 Pro",
      category: "AI-Powered Autonomous Cleaning Robot",
      description: "Advanced AI-powered cleaning with real-time performance detection and monitoring.",
      image: cc1ProImg,
      features: [
        "AI Real-Time Cleaning Performance Detection",
        "AI Cleaning Intensity Control",
        "VSLAM+ Positioning",
        "AI Components Self-Monitoring"
      ],
      detailPage: "/robots/cc1-pro",
      isNew: true
    },
    {
      name: "PUDU MT1",
      category: "AI Powered Robotic Sweeper",
      description: "Award-winning intelligent sweeping robot with AI trash recognition and 24/7 operation. Red Dot Winner 2025.",
      image: mt1ReddotImg,
      features: [
        "Red Dot Design Award Winner 2025",
        "AI Trash Recognition Technology",
        "No Debris Too Big or Small",
        "Rapid Adaptation to Environments",
        "24/7 Continuous Operation"
      ],
      detailPage: "/robots/mt1",
      isNew: true
    },
    {
      name: "PUDU MT1 Vac",
      category: "AI-powered Robotic Sweeper & Vacuum",
      description: "Advanced sweeping and vacuuming robot with AI-powered cleaning intelligence and extended runtime.",
      image: mt1Img,
      features: [
        "Powerful Suction - 55cm cleaning width for maximum coverage",
        "High Efficiency - Advanced sweeping and vacuuming technology",
        "Extended Run-time - 24/7 continuous operation capability",
        "AI-driven Adaptive Cleaning - Smart debris detection and response",
        "Quick-Release Dust Mop Module - Easy maintenance and cleaning",
        "Hand-vacuum Extension - Versatile cleaning in tight spaces"
      ],
      detailPage: "/robots/mt1-vac",
      isNew: true
    },
    {
      name: "PUDU MT1 Max",
      category: "AI-powered 3D Perception Robotic Sweeper",
      description: "High-capacity industrial sweeper with 3D perception for large-scale cleaning operations.",
      image: mt1MaxImg,
      features: [
        "3D Perception Integration",
        "Magic Cleaning Technology",
        "Covers Up to 100,000㎡",
        "24/7 Continuous Operation"
      ],
      detailPage: "/robots/mt1-max",
      isNew: true
    },
    {
      name: "PUDU SH1",
      category: "Smart Upright Scrubber Dryer",
      description: "Professional upright scrubber with 27kg brush pressure, 20000pa suction power, and multidimensional cleaning. Reduces cleaning time by 70% and water usage by 80%.",
      image: sh1Img,
      features: [
        "27kg Brush Pressure & 350rpm Brush Speed - Powerful high-pressure cleaning dissolves tough stains",
        "20000pa Suction Power - Instant scrub-and-dry capability for spotless floors",
        "Air-Liquid-Debris Separation System - Prevents drain blockages and simplifies disposal",
        "Multidimensional Cleaning - Vertical surfaces, dead zones, low-profile spaces, 360° coverage",
        "9 Cleaning Modes - Normal, Heavy Duty, ECO, Low Residue, Soak, Accessory, Water Suction, Brush Max",
        "1100-1600 m²/h Productivity - 44cm working width, 4L tanks, 70-100min runtime",
        "Touch Screen Interface - Component monitoring, consumable tracking, on-screen training",
        "Modular Quick-Release Design - Easy maintenance for brushes, squeegees, tanks, batteries"
      ],
      detailPage: "/robots/sh1",
      isNew: true,
      imageObjectFit: "contain"
    },
    {
      name: "PUDU BG1 Series",
      category: "AI-Native Large Scrubber-Dryer (BG1 / BG1 Pro)",
      description: "AI-native large scrubber-dryer robots for airports, warehouses, hospitals, malls, and large facilities. One-pass sweep & scrub with extendable edge cleaning, AI Magic Cleaning, and 24/7 continuous operation.",
      image: puduBG1Img,
      features: [
        "550 mm scrubbing width, 708 mm with side brushes",
        "Up to 7.5 h runtime, 75 L clean / 60 L waste tanks",
        "AI Magic Cleaning + AI-driven auto-dosing",
        "3D perception enhancement (BG1 Pro: 3D LiDAR + 3D VSLAM fusion)",
        "24/7 continuous operation with optional workstation",
        "Up to 6,000 m²/h in spot cleaning mode",
      ],
      detailPage: "/robots/pudu-bg1",
      isNew: true,
      imageObjectFit: "contain"
    }
  ];

  const xLabRobots: Robot[] = [
    {
      name: "PUDU D5",
      category: "Industry-Grade Autonomous Quadruped",
      description: "Industry-grade autonomous quadruped robot for inspection, delivery, logistics, security, research, and complex terrain environments. NVIDIA-powered 275 TOPS with 360° perception and IP67 all-weather durability.",
      image: puduD5Img,
      features: [
        "NVIDIA Orin + RK3588 — up to 275 TOPS compute",
        "Dual 96-line LiDAR + 4 fisheye cameras (192-line upgrade)",
        "All-terrain: 25 cm steps, 30° climb, 45° descent, 5 m/s",
        "IP67 all-weather, cold-start at −10°C",
        "Voice + 10+ gesture control, intelligent follow mode",
        "Modular: delivery crate, inspection kit, charging station",
      ],
      detailPage: "/robots/pudu-d5",
      isNew: true,
      imageObjectFit: "contain"
    },
    {
      name: "FlashBot Arm",
      category: "Semi-Humanoid Embodied AI Service Robot",
      description: "Next-generation embodied AI robot with dual 7-DOF robotic arms for autonomous delivery and manipulation. Effortlessly operates elevators, doors, and performs complex tasks.",
      image: flashbotArmImg,
      features: [
        "Seamless Navigation - Effortlessly press elevator buttons, swipe access cards and open doors",
        "Independent Operation - Dexterous hands for precise tasks and wide array of responsibilities",
        "User-Friendly Interaction - Multi-modal AI interaction for natural human-robot communication",
        "Safety - Comprehensive vision and tactile sensors with compliant force control algorithms"
      ],
      detailPage: "/robots/flashbot-arm",
      isNew: true,
      isPreOrder: true
    }
  ];

  const RobotCard = ({ robot }: { robot: Robot }) => {
    const [showVideo, setShowVideo] = useState(false);

    return (
      <Card className="glass-effect border-[hsl(var(--line))] overflow-hidden hover:scale-105 transition-all duration-300 relative">
        <div className="aspect-video bg-white/5 overflow-hidden relative">
          {showVideo && robot.video ? (
            <video 
              src={robot.video}
              controls
              autoPlay
              className="w-full h-full object-cover"
            />
          ) : (
            <img 
              src={robot.image} 
              alt={robot.name}
              className={`w-full h-full ${robot.imageObjectFit === "contain" ? "object-contain" : "object-cover"}`}
            />
          )}
          {robot.isPreOrder && (
            <Badge className="absolute top-3 right-3 bg-primary text-primary-foreground font-bold">
              PRE-ORDER OPEN
            </Badge>
          )}
          {robot.isNew && !robot.isPreOrder && (
            <Badge variant="secondary" className="absolute top-3 right-3">
              New
            </Badge>
          )}
        </div>
        <div className="p-6">
          <p className="text-xs text-primary font-semibold mb-1">{robot.category}</p>
          <h3 className="font-orbitron font-bold text-xl mb-3">{robot.name}</h3>
          <p className="text-sm text-muted-foreground mb-4">{robot.description}</p>
          
          <div className="space-y-2 mb-4">
            {robot.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-primary mt-1 flex-shrink-0" strokeWidth={2.5} />
                <span className="text-xs">{feature}</span>
              </div>
            ))}
          </div>

          {robot.video && (
            <Button
              variant="secondary"
              className="w-full mb-2"
              onClick={() => setShowVideo(!showVideo)}
            >
              {showVideo ? "View Image" : "Watch it in action"}
            </Button>
          )}

          {robot.detailPage ? (
            <Button asChild className="w-full">
              <Link to={robot.detailPage}>
                View Details <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          ) : robot.isPreOrder ? (
            <Button asChild className="w-full">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Pre-Order Now <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </Button>
          ) : (
            <Button asChild variant="secondary" className="w-full glow-gold">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Book a Demo <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </Button>
          )}
        </div>
      </Card>
    );
  };

  const categories = [
    { id: "all", label: "All Robots" },
    { id: "humanoid", label: "Humanoid Robots" },
    { id: "commercial-delivery", label: "Commercial Delivery" },
    { id: "commercial-cleaning", label: "Commercial Cleaning" },
    { id: "industrial-delivery", label: "Industrial Delivery" },
    { id: "x-lab", label: "Pudu X-Lab" }
  ];

  const allRobots = [...humanoidRobots, ...commercialDeliveryRobots, ...cleaningRobots, ...industrialDeliveryRobots, ...xLabRobots];

  return (
    <div className="min-h-screen py-8 px-4" style={{
      background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
    }}>
      <SEO
        title="PUDU Service Robots — Delivery, Cleaning & Industrial | Neil AI"
        description="Shop BellaBot, KettyBot, CC1, MT1, SH1, FlashBot and more. Authorized PUDU dealer with financing, deployment, and 24/7 support."
        canonical="https://www.neilai.ai/robots"
      />
      <main className="max-w-7xl mx-auto">
        <div className="neil-hero-wrapper mb-12 text-center">
          <div className="neil-header-row mb-6">
            <div className="neil-nav-icons">
              <Button asChild variant="outline" className="gap-2">
                <Link to="/">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Link>
              </Button>
            </div>
          </div>
          <h1 className="neil-hero-title font-orbitron font-bold text-4xl md:text-6xl mb-4">
            Our <span className="text-primary">Robots</span>
          </h1>
          <p className="neil-hero-subtitle text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            Explore our comprehensive lineup of AI-powered service robots designed for delivery, cleaning, and advanced automation.
          </p>

          {/* Category Filter */}
          <div className="neil-robots-filters flex flex-wrap justify-center gap-3 mb-8">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? "default" : "outline"}
                onClick={() => setActiveCategory(cat.id)}
                className="transition-all"
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Humanoid Robots - PUDU D9 Featured */}
        {(activeCategory === "all" || activeCategory === "humanoid") && (
          <section className="neil-robots-section mb-16">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="neil-robots-title font-orbitron font-bold text-3xl">
                Humanoid Robots <Badge className="ml-3 bg-primary">Pre-Orders Open</Badge>
              </h2>
            </div>
            <p className="neil-robots-subtitle text-muted-foreground mb-6">
              Experience the future of robotics with our revolutionary full-sized bipedal humanoid robot - PUDU D9.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {humanoidRobots.map((robot, idx) => (
                <RobotCard key={idx} robot={robot} />
              ))}
            </div>
          </section>
        )}

        {/* Pudu X-Lab - Advanced AI Robots */}
        {(activeCategory === "all" || activeCategory === "x-lab") && (
          <section className="neil-robots-section mb-16">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="neil-robots-title font-orbitron font-bold text-3xl">
                Pudu X-Lab <Badge className="ml-3 bg-primary">Pre-Orders Available</Badge>
              </h2>
            </div>
            <p className="neil-robots-subtitle text-muted-foreground mb-6">
              Advanced embodied AI service robots with next-generation manipulation capabilities.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {xLabRobots.map((robot, idx) => (
                <RobotCard key={idx} robot={robot} />
              ))}
            </div>
          </section>
        )}

        {/* Commercial Delivery Robots */}
        {(activeCategory === "all" || activeCategory === "commercial-delivery") && (
          <section className="neil-robots-section mb-16">
            <h2 className="neil-robots-title font-orbitron font-bold text-3xl mb-8">
              Commercial Delivery Robots
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {commercialDeliveryRobots.map((robot, idx) => (
                <RobotCard key={idx} robot={robot} />
              ))}
            </div>
          </section>
        )}

        {/* Commercial Cleaning Robots */}
        {(activeCategory === "all" || activeCategory === "commercial-cleaning") && (
          <section className="neil-robots-section mb-16">
            <h2 className="neil-robots-title font-orbitron font-bold text-3xl mb-8">
              Commercial Cleaning Robots
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cleaningRobots.map((robot, idx) => (
                <RobotCard key={idx} robot={robot} />
              ))}
            </div>
          </section>
        )}

        {/* Industrial Delivery Robots */}
        {(activeCategory === "all" || activeCategory === "industrial-delivery") && (
          <section className="neil-robots-section mb-16">
            <h2 className="neil-robots-title font-orbitron font-bold text-3xl mb-8">
              Industrial Delivery Robots
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industrialDeliveryRobots.map((robot, idx) => (
                <RobotCard key={idx} robot={robot} />
              ))}
            </div>
          </section>
        )}

        {/* Stats Section - Verified PUDU global numbers */}
        <section className="mb-16">
          <Card className="glass-effect border-[hsl(var(--line))] p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-orbitron font-bold text-primary mb-2">{allRobots.length}+</div>
                <p className="text-sm text-muted-foreground">Robot Models Offered</p>
              </div>
              <div>
                <div className="text-4xl font-orbitron font-bold text-primary mb-2">120,000+</div>
                <p className="text-sm text-muted-foreground">Robots Shipped Globally</p>
              </div>
              <div>
                <div className="text-4xl font-orbitron font-bold text-primary mb-2">80+</div>
                <p className="text-sm text-muted-foreground">Countries & Regions</p>
              </div>
              <div>
                <div className="text-4xl font-orbitron font-bold text-primary mb-2">4</div>
                <p className="text-sm text-muted-foreground">Major Product Lines</p>
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-6">
              PUDU Robotics global company data. Neil AI Global Solutions is an authorized PUDU robotics partner.
            </p>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Card className="glass-effect border-[hsl(var(--line))] p-8">
            <h2 className="font-orbitron font-bold text-2xl mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-muted-foreground mb-6">
              Schedule a demo to see our robots in action and discover which solution is perfect for your needs.
            </p>
            <Button asChild size="lg" variant="secondary" className="glow-gold">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Book a Demo Today
              </a>
            </Button>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default Robots;
